import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { cx, FormSection, IconArrowLeft, IconChevronRight, PageHeader } from '@timely/ui';
import { useActiveWorkspace } from './WorkspaceProvider';
import { Page, PageFrame } from './WorkspaceShell';
import { paths, settingHref, SETTINGS_GROUPS, visibleSettings } from './navigation';

/**
 * Settings: everything that configures the company rather than working its
 * accounts (PRODUCT-UX-FOUNDATION §11). The Settings page lists the sections
 * this person may open; a section keeps that list beside it on a wide screen,
 * and a way back to it on a narrow one.
 */
export function SettingsLayout() {
    const context = useActiveWorkspace();
    const { pathname } = useLocation();
    const to = paths(context.workspace.slug);
    const section = pathname.slice(to.settings.length).replace(/^\//, '').split('/')[0];

    if (!section) return <Outlet />;

    const sections = visibleSettings(context);
    return (
        <Page width="wide">
            <div className="lg:grid lg:grid-cols-sidenav lg:gap-10">
                <nav aria-label="Settings" className="max-lg:mb-4">
                    <Link to={to.settings} className="lg:hidden inline-flex items-center gap-1.5 text-secondary text-accent-text font-semibold hover:underline">
                        <IconArrowLeft size={14} />All settings
                    </Link>
                    <div className="max-lg:hidden space-y-5 lg:sticky lg:top-7">
                        {SETTINGS_GROUPS.map(group => {
                            const inGroup = sections.filter(s => s.group === group);
                            if (inGroup.length === 0) return null;
                            return (
                                <div key={group}>
                                    <div className="text-caption font-semibold text-label-3 px-3 mb-1">{group}</div>
                                    <ul className="space-y-0.5">
                                        {inGroup.map(s => (
                                            <li key={s.section}>
                                                <NavLink
                                                    to={settingHref(context.workspace.slug, s)}
                                                    className={({ isActive }) => cx(
                                                        'flex items-center min-h-8 px-3 py-1 rounded-control text-body transition-colors duration-100',
                                                        isActive ? 'bg-accent-tint text-accent-text font-semibold' : 'text-label-2 hover:bg-hover hover:text-label',
                                                    )}
                                                >
                                                    {s.label}
                                                </NavLink>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            );
                        })}
                    </div>
                </nav>
                <PageFrame.Provider value={true}>
                    <Outlet />
                </PageFrame.Provider>
            </div>
        </Page>
    );
}

/** The Settings page: every section this person may open, grouped, each saying what it holds. */
export function SettingsHome() {
    const context = useActiveWorkspace();
    const sections = visibleSettings(context);

    return (
        <Page width="standard">
            <PageHeader title="Settings" subtitle="How this company is set up: its profile, people, data and messages." />
            {sections.length === 0 ? (
                <p className="text-body text-label-3">There is nothing here for your role. Ask an owner if you need something changed.</p>
            ) : (
                // One index, read down (2026-10 global sweep): each group's name beside its sections, the groups divided
                // by a hairline, the way the settings pages themselves are laid out — not two columns of cards whose
                // heights never matched.
                <div>
                    {SETTINGS_GROUPS.map(group => {
                        const inGroup = sections.filter(s => s.group === group);
                        if (inGroup.length === 0) return null;
                        return (
                            <FormSection key={group} title={group} wide>
                                <ul className="grid gap-x-8 gap-y-1 md:grid-cols-2">
                                    {inGroup.map(s => (
                                        <li key={s.section}>
                                            <Link
                                                to={settingHref(context.workspace.slug, s)}
                                                className="group block -mx-3 px-3 py-2.5 rounded-control hover:bg-hover focus-visible:bg-hover outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset"
                                            >
                                                <span className="flex items-center gap-1.5 text-body font-semibold text-label group-hover:text-accent-text">
                                                    {s.label}
                                                    <span className="text-label-4 group-hover:text-accent-text transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true"><IconChevronRight size={12} /></span>
                                                </span>
                                                <span className="block text-secondary text-label-3 mt-0.5 leading-snug">{s.summary}</span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </FormSection>
                        );
                    })}
                </div>
            )}
        </Page>
    );
}
